import type { ColumnDef, ExpandedState, Row, RowData, Table } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type * as z from 'zod';

import { extractErrorMessage } from '@/shared/helpers';
import type { Result } from '@/shared/helpers/Result';
import { useStateWithRef } from '@/shared/hooks/use-state-with-ref';

import type { RowClickHandler } from '../../data-table';
import { type UseDataTableProps, useDataTable } from '../../use-data-table';
import { createCellEditingStore, type CellEditingStore } from '../core/create-cell-editing-store';
import { createStagedValueStore, type StagedValueStore } from '../core/create-staged-value-store';
import type {
  EditableCellCommitArgs,
  EditableCellEditRequestArgs,
} from '../core/editable-cell-display';
import { wrapEditableColumns } from '../core/wrap-editable-columns';
import type { EditableTableSchema } from '../types';
import type { RowEditingContextValue } from './row-editing-context';

export type EditableRowPatch<TData extends RowData> = Readonly<Partial<TData>>;
export type EditableRowValues<TSchema extends EditableTableSchema> = Readonly<z.output<TSchema>>;

export type SaveRowFn<TData extends RowData, TValues> = (
  row: TData,
  values: Readonly<TValues>,
) => Promise<void | Result<unknown>>;

export type DeleteRowFn<TData extends RowData> = (row: TData) => Promise<void | Result<unknown>>;

export type StagedRowChangeArgs<TData extends RowData> = {
  rowId: string;
  changedColumnId: string;
  draft: Readonly<TData & Record<string, unknown>>;
  setValues: (values: Partial<TData> | Record<string, unknown>) => void;
};

export type StagedRowChangeFn<TData extends RowData> = (args: StagedRowChangeArgs<TData>) => void;

export type StartEditingRowOptions = {
  columnId?: string;
};

type RequestOpenRowArgs<TData extends RowData> = {
  rowId: string;
  row?: Row<TData>;
  seed?: Partial<TData>;
  discardActiveCell?: boolean;
};

export type UseEditableRowDataTableProps<
  TData extends Record<string, unknown>,
  TSchema extends EditableTableSchema,
> = Omit<UseDataTableProps<TData>, 'columns'> & {
  columns: ColumnDef<TData, any>[];
  schema: TSchema;
  onSaveRow: SaveRowFn<TData, z.output<TSchema>>;
  onDeleteRow?: DeleteRowFn<TData>;
  onStagedRowChange?: StagedRowChangeFn<TData>;
  getCanEditRow?: (row: Row<TData>) => boolean;
};

export type UseEditableRowDataTableResult<TData extends RowData> = {
  mode: 'row';
  table: Table<TData>;
  editingRowId: string | null;
  isSaving: boolean;
  isDeleting: boolean;
  onClickRow: RowClickHandler<TData>;
  onSubmit: () => Promise<void>;
  onDelete?: () => Promise<void>;
  onCancelEdit: () => void;
  startEditingRow: (rowId: string, seed?: Partial<TData>, options?: StartEditingRowOptions) => void;
  getCanEditRow: (row: Row<TData>) => boolean;
  cellEditingStore: CellEditingStore;
  stagedValueStore: StagedValueStore;
  rowEditingContextValue: RowEditingContextValue;
};

export function useEditableRowDataTable<
  TData extends Record<string, unknown>,
  TSchema extends EditableTableSchema = EditableTableSchema,
>({
  columns,
  data,
  getRowId,
  onDeleteRow,
  getCanEditRow,
  onSaveRow,
  onStagedRowChange,
  schema,
  state,
  ...props
}: UseEditableRowDataTableProps<TData, TSchema>): UseEditableRowDataTableResult<TData> {
  const [editingRowId, setEditingRowId, editingRowIdRef] = useStateWithRef<string | null>(null);
  const [isSaving, setIsSaving, isSavingRef] = useStateWithRef(false);
  const [isDeleting, setIsDeleting, isDeletingRef] = useStateWithRef(false);
  const cellEditingStore = useMemo(() => createCellEditingStore(), []);
  const stagedValueStore = useMemo(() => createStagedValueStore(), []);

  const onSaveRowRef = useRef(onSaveRow);
  onSaveRowRef.current = onSaveRow;
  const onDeleteRowRef = useRef(onDeleteRow);
  onDeleteRowRef.current = onDeleteRow;
  const onStagedRowChangeRef = useRef(onStagedRowChange);
  onStagedRowChangeRef.current = onStagedRowChange;
  const getCanEditRowRef = useRef(getCanEditRow);
  getCanEditRowRef.current = getCanEditRow;

  const resolveCanEditRow = useCallback(
    (row: Row<TData>) => getCanEditRowRef.current?.(row) ?? true,
    [],
  );

  const handleStagedRowChange = useCallback(
    ({ row, rowId, columnId }: EditableCellCommitArgs<TData>) => {
      const onStagedRowChange = onStagedRowChangeRef.current;
      if (!onStagedRowChange) return;

      const patchBeforeCallback = stagedValueStore.getRowSnapshot(rowId);
      const draft = Object.freeze({
        ...(row as Record<string, unknown>),
        ...patchBeforeCallback,
      }) as Readonly<TData & Record<string, unknown>>;

      try {
        onStagedRowChange({
          rowId,
          changedColumnId: columnId,
          draft,
          setValues: (values) => {
            for (const [targetColumnId, nextValue] of Object.entries(values)) {
              stagedValueStore.setStagedValue(rowId, targetColumnId, nextValue);
            }
          },
        });
      } catch (error) {
        stagedValueStore.resetRow(rowId, patchBeforeCallback);
        throw error;
      }
    },
    [stagedValueStore],
  );

  const resetEditingRow = useCallback(
    (rowId: string | null) => {
      if (!rowId) return;
      stagedValueStore.resetRow(rowId);
    },
    [stagedValueStore],
  );

  const requestOpenRow = useCallback(
    ({ rowId, row, seed, discardActiveCell = true }: RequestOpenRowArgs<TData>) => {
      if (isSavingRef.current || isDeletingRef.current) return false;

      if (row && !resolveCanEditRow(row)) return false;

      const previousRowId = editingRowIdRef.current;
      // Re-opening the current row intentionally keeps staged values and ignores any new seed.
      if (previousRowId === rowId) return true;

      cellEditingStore.clearPendingCell();

      if (discardActiveCell) {
        cellEditingStore.cancelActiveCell();
      }

      resetEditingRow(previousRowId);
      stagedValueStore.resetRow(rowId, seed);
      setEditingRowId(rowId);
      return true;
    },
    [
      cellEditingStore,
      editingRowIdRef,
      isDeletingRef,
      isSavingRef,
      resetEditingRow,
      resolveCanEditRow,
      setEditingRowId,
      stagedValueStore,
    ],
  );

  const startEditingCell = useCallback(
    ({ row, rowId, cellKey }: EditableCellEditRequestArgs<TData>) => {
      const isCrossRowEdit = editingRowIdRef.current !== null && editingRowIdRef.current !== rowId;

      if (!requestOpenRow({ rowId, row, discardActiveCell: false })) return;

      cellEditingStore.requestOpenCell(
        cellKey,
        isCrossRowEdit ? { discardActiveCell: true } : undefined,
      );
    },
    [cellEditingStore, editingRowIdRef, requestOpenRow],
  );

  const editableColumns = useMemo<ColumnDef<TData, any>[]>(
    () =>
      wrapEditableColumns({
        columns,
        schema,
        mode: 'row',
        onCommit: handleStagedRowChange,
        onRequestEditCell: startEditingCell,
        getCanEditRow: resolveCanEditRow,
      }),
    [columns, handleStagedRowChange, resolveCanEditRow, schema, startEditingCell],
  );

  const tableExpanded = useMemo<ExpandedState | undefined>(() => {
    const consumerExpanded = state?.expanded;
    if (!editingRowId) return consumerExpanded;
    const baseExpanded =
      consumerExpanded && typeof consumerExpanded === 'object' ? consumerExpanded : {};
    return { ...baseExpanded, [editingRowId]: true };
  }, [editingRowId, state?.expanded]);

  const tableState = useMemo(
    () => ({
      ...state,
      ...(tableExpanded !== undefined ? { expanded: tableExpanded } : {}),
    }),
    [state, tableExpanded],
  );

  const editableGetRowId = useCallback<NonNullable<UseDataTableProps<TData>['getRowId']>>(
    (row, index, parent) => {
      const resolvedId = getRowId ? getRowId(row, index, parent) : row.id;
      if (typeof resolvedId !== 'string') {
        throw new Error('useEditableRowDataTable requires each row id to be a stable string');
      }
      return resolvedId;
    },
    [getRowId],
  );

  const table = useDataTable<TData>({
    ...props,
    data,
    columns: editableColumns,
    getRowId: editableGetRowId,
    enableRowExpansion: true,
    state: tableState,
  });

  const rowEditingContextValue = useMemo<RowEditingContextValue>(
    () => ({ editingRowId, isSaving, isDeleting }),
    [editingRowId, isDeleting, isSaving],
  );

  const getEditingTableRow = useCallback((): Row<TData> | null => {
    const rowId = editingRowIdRef.current;
    if (!rowId) return null;
    return table.getRowModel().rowsById[rowId] ?? null;
  }, [editingRowIdRef, table]);

  const startEditingRow = useCallback(
    (rowId: string, seed?: Partial<TData>, options?: StartEditingRowOptions) => {
      const row = table.getRowModel().rowsById[rowId];
      if (!requestOpenRow({ rowId, row, seed })) return;

      if (options?.columnId) {
        const column = table
          .getVisibleLeafColumns()
          .find((visibleColumn) => visibleColumn.id === options.columnId);
        const editableMeta = column?.columnDef.meta?.editable;
        if (!editableMeta || !('editor' in editableMeta)) return;

        cellEditingStore.requestPendingOpenCell(`${rowId}:${options.columnId}`);
      }
    },
    [cellEditingStore, requestOpenRow, table],
  );

  const onClickRow = useCallback<RowClickHandler<TData>>(
    (row) => {
      requestOpenRow({ rowId: row.id, row });
    },
    [requestOpenRow],
  );

  const onCancelEdit = useCallback(() => {
    if (isSavingRef.current || isDeletingRef.current) return;
    cellEditingStore.cancelActiveCell();
    cellEditingStore.clearPendingCell();
    resetEditingRow(editingRowIdRef.current);
    setEditingRowId(null);
  }, [
    cellEditingStore,
    editingRowIdRef,
    isDeletingRef,
    isSavingRef,
    resetEditingRow,
    setEditingRowId,
  ]);

  const onSubmit = useCallback(async () => {
    const rowId = editingRowIdRef.current;
    if (!rowId || isSavingRef.current || isDeletingRef.current) return;
    if (!cellEditingStore.submitActiveCell()) return;

    const editingTableRow = getEditingTableRow();
    if (!editingTableRow || !resolveCanEditRow(editingTableRow)) return;
    const editingRow = editingTableRow.original;

    const patch = stagedValueStore.getRowSnapshot(rowId) as EditableRowPatch<TData>;

    const schemaResult = schema.safeParse(
      Object.fromEntries(
        Object.keys(schema.shape).map((key) => [
          key,
          Object.prototype.hasOwnProperty.call(patch, key) ? patch[key] : editingRow[key],
        ]),
      ),
    );
    if (!schemaResult.success) {
      // Row-save schema failures intentionally have no visual affordance yet.
      // A future task should surface these errors and focus the first invalid cell.
      console.error('Row validation error:', schemaResult.error.message);
      return;
    }

    setIsSaving(true);

    try {
      const result = await onSaveRowRef.current(editingRow, schemaResult.data);
      if (result?.isFailure) return;
      resetEditingRow(rowId);
      cellEditingStore.clearPendingCell();
      setEditingRowId(null);
    } catch (error) {
      console.error('useEditableRowDataTable onSaveRow threw:', extractErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  }, [
    cellEditingStore,
    editingRowIdRef,
    getEditingTableRow,
    isDeletingRef,
    isSavingRef,
    resetEditingRow,
    resolveCanEditRow,
    schema,
    setEditingRowId,
    setIsSaving,
    stagedValueStore,
  ]);

  const onDelete = useMemo(() => {
    if (!onDeleteRow) return undefined;

    return async () => {
      const rowId = editingRowIdRef.current;
      if (!rowId || isSavingRef.current || isDeletingRef.current) return;

      const editingTableRow = getEditingTableRow();
      if (!editingTableRow || !resolveCanEditRow(editingTableRow)) return;

      cellEditingStore.cancelActiveCell();
      cellEditingStore.clearPendingCell();
      setIsDeleting(true);

      try {
        const result = await onDeleteRowRef.current?.(editingTableRow.original);
        if (result?.isFailure) return;
        resetEditingRow(rowId);
        setEditingRowId(null);
      } catch (error) {
        console.error('useEditableRowDataTable onDeleteRow threw:', extractErrorMessage(error));
      } finally {
        setIsDeleting(false);
      }
    };
  }, [
    cellEditingStore,
    editingRowIdRef,
    getEditingTableRow,
    isDeletingRef,
    isSavingRef,
    onDeleteRow,
    resetEditingRow,
    resolveCanEditRow,
    setEditingRowId,
    setIsDeleting,
  ]);

  // After a save/delete settles, abandon editing if the row vanished or became non-editable.
  useEffect(() => {
    if (!editingRowId || isSaving || isDeleting) return;
    const editingTableRow = getEditingTableRow();
    if (!editingTableRow || !resolveCanEditRow(editingTableRow)) {
      cellEditingStore.cancelActiveCell();
      cellEditingStore.clearPendingCell();
      resetEditingRow(editingRowId);
      setEditingRowId(null);
    }
  }, [
    cellEditingStore,
    editingRowId,
    getEditingTableRow,
    isDeleting,
    isSaving,
    resetEditingRow,
    resolveCanEditRow,
    setEditingRowId,
  ]);

  return {
    table,
    mode: 'row',
    editingRowId,
    isSaving,
    isDeleting,
    onClickRow,
    onSubmit,
    onDelete,
    onCancelEdit,
    startEditingRow,
    getCanEditRow: resolveCanEditRow,
    cellEditingStore,
    stagedValueStore,
    rowEditingContextValue,
  };
}
